import React, { useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import './AuroraBackground.css';

const DEFAULT_COLOR_STOPS = [
  { color: '#0066CC', position: 0.0 },
  { color: '#00D4FF', position: 0.33 },
  { color: '#6B46C1', position: 0.66 },
  { color: '#004e92', position: 1.0 },
];

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  uniform float uTime;
  uniform float uAmplitude;
  uniform float uBlend;
  uniform vec3 uColorStops[4];
  uniform float uColorPositions[4];
  uniform vec2 uResolution;

  varying vec2 vUv;

  //  Simplex 3D noise
  vec4 permute(vec4 x){ return mod(((x*34.0)+1.0)*x, 289.0); }
  vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v){
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod(i, 289.0);
    vec4 p = permute(permute(permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 1.0/7.0;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  vec3 getGradientColor(float t) {
    vec3 color = uColorStops[0];
    for (int i = 1; i < 4; i++) {
      float blendFactor = smoothstep(uColorPositions[i - 1], uColorPositions[i], t);
      color = mix(color, uColorStops[i], blendFactor);
    }
    return color;
  }

  void main() {
    vec2 uv = vUv;

    // Aspect-corrected coordinates
    float aspect = uResolution.x / uResolution.y;
    vec2 pos = vec2(uv.x * aspect, uv.y);

    // Multiple noise layers for aurora effect
    float noise1 = snoise(vec3(pos * 1.5, uTime * 0.15)) * uAmplitude;
    float noise2 = snoise(vec3(pos * 3.0, uTime * 0.1 + 100.0)) * uAmplitude * 0.5;
    float noise3 = snoise(vec3(pos * 0.8, uTime * 0.08 + 200.0)) * uAmplitude * 0.8;

    float combinedNoise = noise1 + noise2 + noise3;

    // Map noise to gradient position
    float gradientPos = clamp(uv.y + combinedNoise * 0.3, 0.0, 1.0);
    vec3 color = getGradientColor(gradientPos);

    // Add subtle wave bands
    float wave = sin(uv.y * 10.0 + uTime * 0.3 + noise1 * 3.0) * 0.03;
    color += wave;

    // Vignette
    float vignette = 1.0 - length((uv - 0.5) * 1.2);
    vignette = smoothstep(0.0, 0.7, vignette);

    // Blend with dark background
    vec3 bgColor = vec3(0.0, 0.016, 0.036); // #000428
    color = mix(bgColor, color, uBlend * vignette * 0.6);

    gl_FragColor = vec4(color, 1.0);
  }
`;

function hexToVec3(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return new THREE.Vector3(r, g, b);
}

const AuroraBackground = ({
  colorStops = DEFAULT_COLOR_STOPS,
  amplitude = 1.0,
  speed = 1.0,
  blend = 0.6,
}) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);
  const materialRef = useRef(null);

  const uniforms = useMemo(() => {
    const colors = colorStops.map((s) => hexToVec3(s.color));
    const positions = colorStops.map((s) => s.position);

    // Pad to 4 stops
    while (colors.length < 4) {
      colors.push(colors[colors.length - 1]);
      positions.push(1.0);
    }

    return {
      uTime: { value: 0 },
      uAmplitude: { value: amplitude },
      uBlend: { value: blend },
      uColorStops: { value: colors },
      uColorPositions: { value: positions },
      uResolution: { value: new THREE.Vector2(1, 1) },
    };
  }, [colorStops, amplitude, blend]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    renderer.domElement.className = 'aurora-canvas';
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
    });
    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      uniforms.uResolution.value.set(w, h);
    };
    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    const startTime = performance.now();
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      uniforms.uTime.value = elapsed * speed;
      renderer.render(scene, camera);
      frameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(frameRef.current);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [uniforms, speed]);

  return <div ref={containerRef} className="aurora-container" />;
};

export default AuroraBackground;
